import { Project } from "@/api/query/useProjectQuery";

export interface ProjectNode extends Project {
  type: "project";
}

// A program in the final hierarchy
export interface ProgramNode {
  type: "program";
  programId: string;
  programName: string;
  projects: ProjectNode[];
}

// A portfolio in the final hierarchy
export interface PortfolioNode {
  type: "portfolio";
  portfolioId: string;
  portfolioName: string;
  projects: ProjectNode[]; // Projects directly under the portfolio
  programs: ProgramNode[];
}

// The final data structure for your state is a union of these types
export type HierarchyItem = PortfolioNode | ProgramNode | ProjectNode;

// Internal type used during calculation to hold temporary values
type Calculable<T> = T & {
  __totalDuration: number;
  __weightedProgress: number;
};
function ProjectHierarchy(projectsList: Project[]) {
  const portfolioMap = new Map<string, Calculable<PortfolioNode>>();
  const orphanProgramMap = new Map<string, Calculable<ProgramNode>>();
  const standaloneProjects: ProjectNode[] = [];

  for (const project of projectsList) {
    if (!project.projectId) continue;

    const hasPortfolio = !!project.portfolio?.portfolioId;
    const hasProgram = !!project.program?.programId;

    if (hasPortfolio) {
      const portfolioId = project.portfolio!.portfolioId;
      if (!portfolioMap.has(portfolioId)) {
        portfolioMap.set(portfolioId, {
          type: "portfolio",
          portfolioId,
          portfolioName:
            project.portfolio!.portfolioName || "Unnamed Portfolio",
          projects: [],
          programs: [],
          __totalDuration: 0,
          __weightedProgress: 0,
        });
      }
      const portfolioEntry = portfolioMap.get(portfolioId)!;

      if (hasProgram) {
        const programId = project.program!.programId;
        let programEntry = portfolioEntry.programs.find(
          (p) => p.programId === programId
        ) as Calculable<ProgramNode> | undefined;

        if (!programEntry) {
          programEntry = {
            type: "program",
            programId,
            programName: project.program!.programName || "Unnamed Program",
            projects: [],
            __totalDuration: 0,
            __weightedProgress: 0,
          };
          portfolioEntry.programs.push(programEntry as ProgramNode);
        }
        programEntry.projects.push(project as ProjectNode);
      } else {
        portfolioEntry.projects.push(project as ProjectNode);
      }
    } else if (hasProgram) {
      const programId = project.program!.programId;
      if (!orphanProgramMap.has(programId)) {
        orphanProgramMap.set(programId, {
          type: "program",
          programId,
          programName: project.program!.programName || "Unnamed Program",
          projects: [],
          __totalDuration: 0,
          __weightedProgress: 0,
        });
      }
      const programEntry = orphanProgramMap.get(programId)!;

      programEntry.projects.push(project as ProjectNode);
    } else {
      standaloneProjects.push(project as ProjectNode);
    }
  }

  const finalPortfolios = Array.from(portfolioMap.values()).map((p) => {
    p.programs.forEach((prog) => {
      const program = prog as Calculable<ProgramNode>;

      delete (program as any).__totalDuration;
      delete (program as any).__weightedProgress;
    });

    delete (p as any).__totalDuration;
    delete (p as any).__weightedProgress;
    return p;
  });

  const finalOrphanPrograms = Array.from(orphanProgramMap.values()).map((p) => {
    delete (p as any).__totalDuration;
    delete (p as any).__weightedProgress;
    return p;
  });

  return [...finalPortfolios, ...finalOrphanPrograms, ...standaloneProjects];
}
export default function calculatePortfolioAndProgramSPIs(
  projectList: Project[]
): Record<string, number> {
  const items: HierarchyItem[] = ProjectHierarchy(projectList);
  const spiMap: Record<string, number> = {};


  for (const item of items) {
    if (item.type === "portfolio") {
      // Calculate portfolio SPI
      const allProjects: ProjectNode[] = [
        ...item.projects,
        ...item.programs.flatMap((p) => p.projects),
      ];
      spiMap[item.portfolioId] = calculateSPI(allProjects);

      // Also calculate SPI per program inside the portfolio
      for (const program of item.programs) {
        spiMap[program.programId] = calculateSPI(program.projects);
      }
    } else if (item.type === "program") {
      // For stand-alone programs
      spiMap[item.programId] = calculateSPI(item.projects);
    }
  }

  return spiMap;
}

export const calculateSPI = (projects: ProjectNode[]) => {
    let weightedSum = 0;
    let totalDuration = 0;

    for (const project of projects) {
      if (
        typeof project.spi === "number" &&
        typeof project.actualDuration === "number"
      ) {
        weightedSum += project.spi * project.actualDuration;
        totalDuration += project.actualDuration;
      }
    }

    return weightedSum / totalDuration;
  };